import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Plus,
  Trash2,
  Copy,
  Save,
  Search,
  CheckSquare,
  Square,
  UserCheck,
  Layers,
  Eye,
  Edit3,
  FileCheck2,
  Download,
  Key,
} from 'lucide-react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Loading,
  PageHeader,
  Panel,
  PanelBody,
  SectionLabel,
  Table,
  TableShell,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.js';
import { fetchJson } from '../api.js';

interface PageDefinition {
  readonly id: string;
  readonly module: string;
  readonly path: string;
  readonly title: string;
  readonly description: string;
  readonly code: string;
}

interface PermissionFlags {
  view: boolean;
  write: boolean;
  decide: boolean;
  export: boolean;
  admin: boolean;
}

interface RoleDefinition {
  id: string;
  name: string;
  description: string;
  baseRole: 'admin' | 'reviewer' | 'shop_manager' | 'supply_chain' | 'viewer';
  color: string;
  isCustom: boolean;
}

interface RbacData {
  roles: RoleDefinition[];
  permissions: Record<string, Record<string, PermissionFlags>>;
  pages: PageDefinition[];
  userRoles: Record<string, string>;
  updatedAt: string;
}

interface UserRow {
  readonly id: number;
  readonly email: string;
  readonly name: string;
  readonly role: 'viewer' | 'reviewer' | 'admin' | 'shop_manager' | 'supply_chain';
  readonly customRoleId?: string | null;
  readonly isActive: boolean;
}

interface UserList {
  readonly users: readonly UserRow[];
}

const COLOR_CLASSES: Record<string, { badge: string; tab: string; ring: string }> = {
  sky: { badge: 'bg-sky-50 text-sky-900 border-sky-300', tab: 'bg-sky-50 text-sky-900 border-sky-300', ring: 'ring-sky-500' },
  amber: { badge: 'bg-amber-50 text-amber-900 border-amber-300', tab: 'bg-amber-50 text-amber-900 border-amber-300', ring: 'ring-amber-500' },
  emerald: { badge: 'bg-emerald-50 text-emerald-900 border-emerald-300', tab: 'bg-emerald-50 text-emerald-900 border-emerald-300', ring: 'ring-emerald-500' },
  purple: { badge: 'bg-purple-50 text-purple-900 border-purple-300', tab: 'bg-purple-50 text-purple-900 border-purple-300', ring: 'ring-purple-500' },
  slate: { badge: 'bg-slate-100 text-slate-800 border-slate-300', tab: 'bg-slate-100 text-slate-800 border-slate-300', ring: 'ring-slate-500' },
  indigo: { badge: 'bg-indigo-50 text-indigo-900 border-indigo-300', tab: 'bg-indigo-50 text-indigo-900 border-indigo-300', ring: 'ring-indigo-500' },
  rose: { badge: 'bg-rose-50 text-rose-900 border-rose-300', tab: 'bg-rose-50 text-rose-900 border-rose-300', ring: 'ring-rose-500' },
  teal: { badge: 'bg-teal-50 text-teal-900 border-teal-300', tab: 'bg-teal-50 text-teal-900 border-teal-300', ring: 'ring-teal-500' },
  cyan: { badge: 'bg-cyan-50 text-cyan-900 border-cyan-300', tab: 'bg-cyan-50 text-cyan-900 border-cyan-300', ring: 'ring-cyan-500' },
  orange: { badge: 'bg-orange-50 text-orange-900 border-orange-300', tab: 'bg-orange-50 text-orange-900 border-orange-300', ring: 'ring-orange-500' },
};

const DEFAULT_ROLE_COLORS = { badge: 'bg-indigo-50 text-indigo-900 border-indigo-300', tab: 'bg-indigo-50 text-indigo-900 border-indigo-300', ring: 'ring-indigo-500' };

function getRoleColors(color: string): { badge: string; tab: string; ring: string } {
  return COLOR_CLASSES[color] ?? DEFAULT_ROLE_COLORS;
}

export function SettingsPermissionsPage(): React.ReactElement {
  const queryClient = useQueryClient();

  const rbacQuery = useQuery({
    queryKey: ['rbac_permissions'],
    queryFn: () => fetchJson<RbacData>('/api/v1/rbac/permissions'),
  });

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => fetchJson<UserList>('/api/v1/users'),
  });

  // Local state for permissions matrix edits
  const [localPermissions, setLocalPermissions] = useState<Record<string, Record<string, PermissionFlags>> | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('reviewer');
  const [activeTab, setActiveTab] = useState<'editor' | 'matrix'>('editor');
  const [search, setSearch] = useState('');
  const [selectedModule, setSelectedModule] = useState<string>('all');
  const [isDirty, setIsDirty] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Modal: New Role / Account Type
  const [isCreateRoleOpen, setIsCreateRoleOpen] = useState(false);
  const [newRoleForm, setNewRoleForm] = useState({
    id: '',
    name: '',
    description: '',
    baseRole: 'reviewer' as RoleDefinition['baseRole'],
    color: 'indigo',
    cloneFromRoleId: 'reviewer',
  });

  // Modal: Copy from role
  const [copySourceRole, setCopySourceRole] = useState<string>('reviewer');
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);

  // Initialize or sync localPermissions from query
  const permissions = localPermissions ?? rbacQuery.data?.permissions ?? {};
  const roles = rbacQuery.data?.roles ?? [];
  const pages = rbacQuery.data?.pages ?? [];
  const users = usersQuery.data?.users ?? [];
  const userRoles = rbacQuery.data?.userRoles ?? {};

  // Active role definition
  const selectedRole = roles.find((r) => r.id === selectedRoleId) ?? roles[0] ?? {
    id: 'reviewer',
    name: 'Reviewer',
    description: '',
    baseRole: 'reviewer',
    color: 'amber',
    isCustom: false,
  };

  // Users belonging to selected role
  const usersWithRole = users.filter((u) => {
    const customRole = u.customRoleId ?? userRoles[String(u.id)];
    if (selectedRole.isCustom) {
      return customRole === selectedRole.id && u.isActive;
    }
    // For built-in roles, only users who don't have a custom role mapped
    return u.role === selectedRole.id && (!customRole || customRole === selectedRole.id) && u.isActive;
  });

  // Available Modules list
  const modules = useMemo(() => {
    const set = new Set<string>();
    for (const page of pages) {
      set.add(page.module);
    }
    return Array.from(set);
  }, [pages]);

  // Filtered pages
  const filteredPages = useMemo(() => {
    let result = pages;
    if (selectedModule !== 'all') {
      result = result.filter((p) => p.module === selectedModule);
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.path.toLowerCase().includes(q) ||
          p.code.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.module.toLowerCase().includes(q)
      );
    }
    return result;
  }, [pages, selectedModule, search]);

  // Save full configuration mutation
  const saveMutation = useMutation({
    mutationFn: (payload: { roles: RoleDefinition[]; permissions: Record<string, Record<string, PermissionFlags>>; userRoles: Record<string, string> }) =>
      fetchJson('/api/v1/rbac/permissions', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      setIsDirty(false);
      setSaveSuccess(true);
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['rbac_permissions'] });
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (err: Error) => {
      setActionError(`Erreur de sauvegarde: ${err.message}`);
    },
  });

  // Create custom role mutation
  const createRoleMutation = useMutation<{ role: RoleDefinition }, Error, typeof newRoleForm>({
    mutationFn: (payload: typeof newRoleForm) =>
      fetchJson<{ role: RoleDefinition }>('/api/v1/rbac/roles', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: (data: { role: RoleDefinition }) => {
      setIsCreateRoleOpen(false);
      setSelectedRoleId(data.role.id);
      setIsDirty(false);
      setActionError(null);
      setNewRoleForm({
        id: '',
        name: '',
        description: '',
        baseRole: 'reviewer',
        color: 'indigo',
        cloneFromRoleId: 'reviewer',
      });
      void queryClient.invalidateQueries({ queryKey: ['rbac_permissions'] });
    },
    onError: (err: Error) => {
      setActionError(`Impossible de créer le rôle: ${err.message}`);
    },
  });

  // Delete custom role mutation
  const deleteRoleMutation = useMutation({
    mutationFn: (roleId: string) =>
      fetchJson(`/api/v1/rbac/roles/${roleId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      setSelectedRoleId('reviewer');
      setIsDirty(false);
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['rbac_permissions'] });
    },
    onError: (err: Error) => {
      setActionError(`Impossible de supprimer le rôle: ${err.message}`);
    },
  });

  // Helper to mutate permissions in state
  const handleTogglePermission = (roleId: string, pagePath: string, key: keyof PermissionFlags) => {
    const currentMatrix = localPermissions ? { ...localPermissions } : { ...permissions };
    if (!currentMatrix[roleId]) {
      currentMatrix[roleId] = {};
    }
    const currentFlags = currentMatrix[roleId][pagePath] ?? {
      view: false,
      write: false,
      decide: false,
      export: false,
      admin: false,
    };
    currentMatrix[roleId] = {
      ...currentMatrix[roleId],
      [pagePath]: {
        ...currentFlags,
        [key]: !currentFlags[key],
      },
    };
    setLocalPermissions(currentMatrix);
    setIsDirty(true);
  };

  // Helper to toggle entire row
  const handleToggleRowAll = (roleId: string, pagePath: string) => {
    const currentMatrix = localPermissions ? { ...localPermissions } : { ...permissions };
    if (!currentMatrix[roleId]) currentMatrix[roleId] = {};
    const flags = currentMatrix[roleId][pagePath];
    const allChecked = flags && flags.view && flags.write && flags.decide && flags.export && flags.admin;
    const targetState = !allChecked;

    currentMatrix[roleId] = {
      ...currentMatrix[roleId],
      [pagePath]: {
        view: targetState,
        write: targetState,
        decide: targetState,
        export: targetState,
        admin: targetState,
      },
    };
    setLocalPermissions(currentMatrix);
    setIsDirty(true);
  };

  // Batch toggle all pages for selected role
  const handleBatchToggleAll = (targetState: boolean) => {
    const currentMatrix = localPermissions ? { ...localPermissions } : { ...permissions };
    if (!currentMatrix[selectedRoleId]) currentMatrix[selectedRoleId] = {};
    const updatedRolePerms = { ...currentMatrix[selectedRoleId] };

    for (const page of pages) {
      updatedRolePerms[page.path] = {
        view: targetState,
        write: targetState,
        decide: targetState,
        export: targetState,
        admin: targetState,
      };
    }
    currentMatrix[selectedRoleId] = updatedRolePerms;
    setLocalPermissions(currentMatrix);
    setIsDirty(true);
  };

  // Batch toggle all pages in a module for selected role
  const handleBatchToggleModule = (moduleName: string, targetState: boolean) => {
    const currentMatrix = localPermissions ? { ...localPermissions } : { ...permissions };
    if (!currentMatrix[selectedRoleId]) currentMatrix[selectedRoleId] = {};
    const updatedRolePerms = { ...currentMatrix[selectedRoleId] };

    const modulePages = pages.filter((p) => p.module === moduleName);
    for (const page of modulePages) {
      updatedRolePerms[page.path] = {
        view: targetState,
        write: targetState,
        decide: targetState,
        export: targetState,
        admin: targetState,
      };
    }
    currentMatrix[selectedRoleId] = updatedRolePerms;
    setLocalPermissions(currentMatrix);
    setIsDirty(true);
  };

  // Copy permissions from another role
  const handleApplyCopyPermissions = () => {
    if (!copySourceRole || copySourceRole === selectedRoleId) return;
    const currentMatrix = localPermissions ? { ...localPermissions } : { ...permissions };
    const sourcePerms = currentMatrix[copySourceRole];
    if (sourcePerms) {
      currentMatrix[selectedRoleId] = JSON.parse(JSON.stringify(sourcePerms));
      setLocalPermissions(currentMatrix);
      setIsDirty(true);
      setIsCopyModalOpen(false);
    }
  };

  // Discard changes
  const handleDiscardChanges = () => {
    setLocalPermissions(null);
    setIsDirty(false);
  };

  // Trigger Save
  const handleSave = () => {
    const permsToSave = localPermissions ?? permissions;
    saveMutation.mutate({
      roles,
      permissions: permsToSave,
      userRoles,
    });
  };

  if (rbacQuery.isPending) {
    return <Loading label="Chargement de la matrice de permissions RBAC…" />;
  }

  const roleColors = getRoleColors(selectedRole.color);
  const currentRolePerms = permissions[selectedRoleId] ?? {};

  // Count how many pages have 'view' allowed for the selected role
  const allowedPagesCount = pages.filter((p) => currentRolePerms[p.path]?.view).length;
  const coveragePercent = pages.length > 0 ? Math.round((allowedPagesCount / pages.length) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header with Title and Global Actions */}
      <PageHeader
        title="Permissions & Rôles (RBAC)"
        hint="Créez des types de comptes personnalisés et contrôlez de façon granulaire les permissions d'accès, d'écriture, d'approbation et d'export pour chaque page de l'application."
        badge={
          <Badge tone="scan">
            {roles.length} rôles · {pages.length} pages
          </Badge>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsCreateRoleOpen(true)}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4 text-herb-600" />
              <span>Nouveau Rôle / Type de compte</span>
            </Button>

            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={saveMutation.isPending || (!isDirty && !saveSuccess)}
              className={`gap-1.5 ${isDirty ? 'ring-2 ring-herb-500 animate-pulse' : ''}`}
            >
              <Save className="h-4 w-4" />
              <span>{saveMutation.isPending ? 'Enregistrement…' : isDirty ? 'Enregistrer les modifications' : 'Permissions à jour'}</span>
            </Button>
          </div>
        }
      />

      {/* Unsaved Changes Banner */}
      {isDirty && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50/90 p-3.5 text-xs text-amber-900 shadow-xs">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
            <span className="font-semibold">
              Vous avez des modifications de permissions non enregistrées pour le rôle <strong>{selectedRole.name}</strong>.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="xs" onClick={handleDiscardChanges}>
              Annuler les changements
            </Button>
            <Button variant="primary" size="xs" onClick={handleSave} disabled={saveMutation.isPending}>
              Enregistrer maintenant
            </Button>
          </div>
        </div>
      )}

      {/* Save Success Notice */}
      {saveSuccess && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-xs font-semibold text-emerald-900">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <span>Matrice des permissions RBAC enregistrée et déployée avec succès.</span>
        </div>
      )}

      {/* Error Notice */}
      {actionError && (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-rose-300 bg-rose-50 p-3 text-xs font-semibold text-rose-900">
          <span>{actionError}</span>
          <button type="button" onClick={() => setActionError(null)} className="text-rose-700 hover:underline">
            Fermer
          </button>
        </div>
      )}

      {/* Role Selection Bar */}
      <Panel>
        <PanelBody className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionLabel>Sélectionnez un Rôle ou Type de Compte à configurer</SectionLabel>
            <div className="flex items-center gap-1.5 text-xs">
              <button
                type="button"
                onClick={() => setActiveTab('editor')}
                className={`rounded-lg px-2.5 py-1 font-bold transition-all ${
                  activeTab === 'editor'
                    ? 'bg-herb-700 text-white shadow-2xs'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Éditeur Détaillé par Rôle
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('matrix')}
                className={`rounded-lg px-2.5 py-1 font-bold transition-all ${
                  activeTab === 'matrix'
                    ? 'bg-herb-700 text-white shadow-2xs'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Matrice Synthétique Complète
              </button>
            </div>
          </div>

          {/* Role Pills */}
          <div className="flex flex-wrap gap-2">
            {roles.map((role) => {
              const colors = getRoleColors(role.color);
              const isSelected = selectedRoleId === role.id;
              const userCount = users.filter((u) => {
                const customRole = u.customRoleId ?? userRoles[String(u.id)];
                if (role.isCustom) return customRole === role.id && u.isActive;
                return u.role === role.id && (!customRole || customRole === role.id) && u.isActive;
              }).length;

              return (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => setSelectedRoleId(role.id)}
                  className={`group relative flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold transition-all ${
                    isSelected
                      ? `${colors.tab} shadow-xs ring-2 ${colors.ring}`
                      : 'border-border bg-card text-ink-soft hover:bg-slate-50'
                  }`}
                >
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      role.color === 'sky'
                        ? 'bg-sky-500'
                        : role.color === 'amber'
                          ? 'bg-amber-500'
                          : role.color === 'emerald'
                            ? 'bg-emerald-500'
                            : role.color === 'purple'
                              ? 'bg-purple-500'
                              : role.color === 'rose'
                                ? 'bg-rose-500'
                                : role.color === 'teal'
                                  ? 'bg-teal-500'
                                  : role.color === 'cyan'
                                    ? 'bg-cyan-500'
                                    : role.color === 'orange'
                                      ? 'bg-orange-500'
                                      : 'bg-indigo-500'
                    }`}
                  />
                  <span>{role.name}</span>
                  {role.isCustom && (
                    <span className="rounded-full bg-herb-100 px-1.5 py-0.2 text-[9px] font-extrabold uppercase tracking-wider text-herb-800">
                      Personnalisé
                    </span>
                  )}
                  <span className="ml-0.5 rounded-full bg-black/5 px-1.5 py-0.5 text-[10px] font-medium text-ink-muted">
                    {userCount}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Selected Role Card with Details and Batch Actions */}
          <div className="rounded-xl border border-border bg-slate-50/70 p-4 space-y-3.5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="h-5 w-5 text-herb-700" />
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-ink">{selectedRole.name}</h3>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${roleColors.badge}`}>
                      Base API: {selectedRole.baseRole}
                    </span>
                    {selectedRole.isCustom && (
                      <span className="rounded-full bg-herb-100 px-2 py-0.5 text-[10px] font-bold text-herb-800 border border-herb-300">
                        Type de compte personnalisé
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-ink-soft mt-0.5">
                    {selectedRole.description || 'Aucune description spécifique renseignée.'}
                  </p>
                </div>
              </div>

              {/* Action buttons for this role */}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => setIsCopyModalOpen(true)}
                  className="gap-1 text-slate-700"
                >
                  <Copy className="h-3 w-3" />
                  <span>Copier d&apos;un autre rôle</span>
                </Button>

                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => handleBatchToggleAll(true)}
                  className="gap-1 text-emerald-800 hover:bg-emerald-50"
                >
                  <CheckSquare className="h-3 w-3 text-emerald-600" />
                  <span>Tout autoriser</span>
                </Button>

                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => handleBatchToggleAll(false)}
                  className="gap-1 text-rose-800 hover:bg-rose-50"
                >
                  <Square className="h-3 w-3 text-rose-600" />
                  <span>Tout interdire</span>
                </Button>

                {selectedRole.isCustom && (
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() => {
                      if (confirm(`Êtes-vous sûr de vouloir supprimer définitivement le rôle personnalisé "${selectedRole.name}" ?`)) {
                        deleteRoleMutation.mutate(selectedRole.id);
                      }
                    }}
                    className="gap-1 text-rose-700 hover:bg-rose-100 border-rose-300"
                  >
                    <Trash2 className="h-3 w-3 text-rose-600" />
                    <span>Supprimer ce rôle</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Coverage KPI & Assigned Users */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border">
              <div className="flex items-center gap-3">
                <span className="text-xs text-ink-soft">
                  Pages accessibles : <strong>{allowedPagesCount} / {pages.length}</strong> ({coveragePercent}%)
                </span>
                <div className="h-2 w-32 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className="h-full bg-herb-600 transition-all duration-300"
                    style={{ width: `${coveragePercent}%` }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-ink-soft font-semibold">
                  Opérateurs actifs ({usersWithRole.length}) :
                </span>
                <div className="flex flex-wrap items-center gap-1.5">
                  {usersWithRole.slice(0, 5).map((u) => (
                    <span
                      key={u.id}
                      className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-ink border border-slate-300 shadow-2xs"
                    >
                      <UserCheck className="h-3 w-3 text-emerald-600" />
                      {u.name}
                    </span>
                  ))}
                  {usersWithRole.length > 5 && (
                    <span className="text-xs text-ink-muted">+{usersWithRole.length - 5} autres</span>
                  )}
                  {usersWithRole.length === 0 && (
                    <span className="text-xs text-ink-ghost italic">Aucun utilisateur assigné</span>
                  )}
                  <Link
                    to="/settings/users"
                    className="text-xs font-bold text-herb-700 hover:underline ml-1"
                  >
                    Gérer
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Main Content Area: Tab 1 (Role Editor) or Tab 2 (Full Matrix) */}
      {activeTab === 'editor' ? (
        <Panel>
          <PanelBody className="space-y-4">
            {/* Filter and Search Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="relative flex-1 min-w-[240px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filtrer par nom de page, route (/inventory) ou code (S30)..."
                  className="w-full rounded-xl border border-border bg-card pl-9 pr-3 py-1.5 text-xs font-medium text-ink focus:border-herb-600 focus:outline-none"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-ink-soft hover:text-ink"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Module Filter Pills */}
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setSelectedModule('all')}
                  className={`rounded-lg px-2.5 py-1 text-xs font-semibold transition-all ${
                    selectedModule === 'all'
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Tous ({pages.length})
                </button>
                {modules.map((mod) => (
                  <button
                    key={mod}
                    type="button"
                    onClick={() => setSelectedModule(mod)}
                    className={`rounded-lg px-2 py-1 text-xs font-semibold transition-all ${
                      selectedModule === mod
                        ? 'bg-herb-700 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {mod}
                  </button>
                ))}
              </div>
            </div>

            {/* Permissions Table for Selected Role */}
            <TableShell>
              <Table>
                <THead>
                  <Tr>
                    <Th className="w-[38%]">Page / Fonctionnalité</Th>
                    <Th className="text-center w-[11%]">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="flex items-center gap-1">
                          <Eye className="h-3.5 w-3.5 text-sky-600" />
                          <span>Vue</span>
                        </span>
                        <span className="text-[10px] font-normal text-ink-ghost">Accès page</span>
                      </div>
                    </Th>
                    <Th className="text-center w-[11%]">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="flex items-center gap-1">
                          <Edit3 className="h-3.5 w-3.5 text-amber-600" />
                          <span>Écriture</span>
                        </span>
                        <span className="text-[10px] font-normal text-ink-ghost">Modif / Saisie</span>
                      </div>
                    </Th>
                    <Th className="text-center w-[11%]">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="flex items-center gap-1">
                          <FileCheck2 className="h-3.5 w-3.5 text-emerald-600" />
                          <span>Décision</span>
                        </span>
                        <span className="text-[10px] font-normal text-ink-ghost">Validation</span>
                      </div>
                    </Th>
                    <Th className="text-center w-[11%]">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="flex items-center gap-1">
                          <Download className="h-3.5 w-3.5 text-purple-600" />
                          <span>Export</span>
                        </span>
                        <span className="text-[10px] font-normal text-ink-ghost">Sage & Woo</span>
                      </div>
                    </Th>
                    <Th className="text-center w-[11%]">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="flex items-center gap-1">
                          <Key className="h-3.5 w-3.5 text-rose-600" />
                          <span>Admin</span>
                        </span>
                        <span className="text-[10px] font-normal text-ink-ghost">Gestion</span>
                      </div>
                    </Th>
                    <Th className="text-center w-[7%]">Actions</Th>
                  </Tr>
                </THead>
                <TBody>
                  {filteredPages.map((page, idx) => {
                    const isNewModule = idx === 0 || filteredPages[idx - 1]?.module !== page.module;
                    const flags = currentRolePerms[page.path] ?? {
                      view: false,
                      write: false,
                      decide: false,
                      export: false,
                      admin: false,
                    };
                    const isAllChecked = flags.view && flags.write && flags.decide && flags.export && flags.admin;

                    return (
                      <>
                        {isNewModule && (
                          <Tr key={`module-${page.module}`} className="bg-slate-100/80 border-t-2 border-slate-300">
                            <Td colSpan={7} className="py-2 px-3">
                              <div className="flex items-center justify-between">
                                <span className="font-extrabold text-xs uppercase tracking-wider text-slate-800 flex items-center gap-2">
                                  <Layers className="h-3.5 w-3.5 text-slate-500" />
                                  <span>{page.module}</span>
                                </span>
                                <div className="flex items-center gap-2 text-[11px]">
                                  <button
                                    type="button"
                                    onClick={() => handleBatchToggleModule(page.module, true)}
                                    className="font-bold text-herb-700 hover:underline"
                                  >
                                    Tout cocher
                                  </button>
                                  <span className="text-slate-300">·</span>
                                  <button
                                    type="button"
                                    onClick={() => handleBatchToggleModule(page.module, false)}
                                    className="font-bold text-rose-700 hover:underline"
                                  >
                                    Tout décocher
                                  </button>
                                </div>
                              </div>
                            </Td>
                          </Tr>
                        )}
                        <Tr
                          key={page.id}
                          className={`hover:bg-slate-50/80 transition-colors ${flags.view ? '' : 'opacity-70 bg-slate-50/30'}`}
                        >
                          <Td className="py-2.5">
                            <div className="flex items-start gap-2.5">
                              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-700 border border-slate-300 shrink-0">
                                {page.code}
                              </span>
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs font-bold text-ink">{page.title}</span>
                                  <span className="font-mono text-[10px] text-ink-ghost">{page.path}</span>
                                </div>
                                <p className="text-[11px] text-ink-soft leading-tight mt-0.5 line-clamp-1">
                                  {page.description}
                                </p>
                              </div>
                            </div>
                          </Td>

                          {/* View Checkbox */}
                          <Td className="text-center py-2.5">
                            <input
                              type="checkbox"
                              checked={Boolean(flags.view)}
                              onChange={() => handleTogglePermission(selectedRoleId, page.path, 'view')}
                              className="h-4 w-4 rounded border-slate-300 text-herb-600 focus:ring-herb-500 cursor-pointer accent-herb-600"
                              title="Autoriser la consultation de cette page"
                            />
                          </Td>

                          {/* Write Checkbox */}
                          <Td className="text-center py-2.5">
                            <input
                              type="checkbox"
                              checked={Boolean(flags.write)}
                              onChange={() => handleTogglePermission(selectedRoleId, page.path, 'write')}
                              className="h-4 w-4 rounded border-slate-300 text-herb-600 focus:ring-herb-500 cursor-pointer accent-herb-600"
                              title="Autoriser la modification et saisie de données"
                            />
                          </Td>

                          {/* Decide Checkbox */}
                          <Td className="text-center py-2.5">
                            <input
                              type="checkbox"
                              checked={Boolean(flags.decide)}
                              onChange={() => handleTogglePermission(selectedRoleId, page.path, 'decide')}
                              className="h-4 w-4 rounded border-slate-300 text-herb-600 focus:ring-herb-500 cursor-pointer accent-herb-600"
                              title="Autoriser la validation, arbitrage ou lettrage"
                            />
                          </Td>

                          {/* Export Checkbox */}
                          <Td className="text-center py-2.5">
                            <input
                              type="checkbox"
                              checked={Boolean(flags.export)}
                              onChange={() => handleTogglePermission(selectedRoleId, page.path, 'export')}
                              className="h-4 w-4 rounded border-slate-300 text-herb-600 focus:ring-herb-500 cursor-pointer accent-herb-600"
                              title="Autoriser les exports comptables et la poussée en direct"
                            />
                          </Td>

                          {/* Admin Checkbox */}
                          <Td className="text-center py-2.5">
                            <input
                              type="checkbox"
                              checked={Boolean(flags.admin)}
                              onChange={() => handleTogglePermission(selectedRoleId, page.path, 'admin')}
                              className="h-4 w-4 rounded border-slate-300 text-herb-600 focus:ring-herb-500 cursor-pointer accent-herb-600"
                              title="Autoriser les actions d'administration sensible"
                            />
                          </Td>

                          {/* Row Action: Toggle All */}
                          <Td className="text-center py-2.5">
                            <button
                              type="button"
                              onClick={() => handleToggleRowAll(selectedRoleId, page.path)}
                              className="text-[11px] font-bold text-slate-500 hover:text-herb-700 underline"
                              title={isAllChecked ? 'Tout désélectionner' : 'Tout sélectionner pour cette page'}
                            >
                              {isAllChecked ? 'Vider' : 'Tout'}
                            </button>
                          </Td>
                        </Tr>
                      </>
                    );
                  })}
                </TBody>
              </Table>
            </TableShell>
          </PanelBody>
        </Panel>
      ) : (
        /* Tab 2: Full Matrix Synthetic View across all roles */
        <Panel>
          <PanelBody className="space-y-4">
            <div className="flex items-center justify-between">
              <SectionLabel>Matrice Comparative Complète (Tous les rôles)</SectionLabel>
              <span className="text-xs text-ink-soft">
                Cochez ou décochez les accès principaux pour chaque profil opérateur.
              </span>
            </div>

            <TableShell>
              <Table>
                <THead>
                  <Tr>
                    <Th className="w-[32%]">Page / Fonctionnalité</Th>
                    {roles.map((r) => (
                      <Th key={r.id} className="text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-xs font-bold">{r.name}</span>
                          <span className="text-[10px] font-normal text-ink-ghost">{r.baseRole}</span>
                        </div>
                      </Th>
                    ))}
                  </Tr>
                </THead>
                <TBody>
                  {pages.map((page, idx) => {
                    const isNewModule = idx === 0 || pages[idx - 1]?.module !== page.module;
                    return (
                      <>
                        {isNewModule && (
                          <Tr key={`module-full-${page.module}`} className="bg-slate-100/80">
                            <Td colSpan={roles.length + 1} className="py-1.5 px-3 font-extrabold text-xs uppercase tracking-wider text-slate-800">
                              {page.module}
                            </Td>
                          </Tr>
                        )}
                        <Tr key={page.id} className="hover:bg-slate-50">
                          <Td className="py-2">
                            <div className="flex items-center gap-2">
                              <span className="rounded bg-slate-100 px-1 py-0.2 font-mono text-[10px] font-bold text-slate-700">
                                {page.code}
                              </span>
                              <span className="text-xs font-semibold text-ink">{page.title}</span>
                            </div>
                          </Td>
                          {roles.map((role) => {
                            const rPerms = permissions[role.id]?.[page.path];
                            const isView = rPerms?.view;
                            const isWrite = rPerms?.write;
                            const isDecide = rPerms?.decide;
                            const isExport = rPerms?.export;

                            return (
                              <Td key={role.id} className="text-center py-2">
                                <button
                                  type="button"
                                  onClick={() => handleTogglePermission(role.id, page.path, 'view')}
                                  className="inline-flex items-center justify-center p-1 rounded hover:bg-slate-100 transition-colors"
                                  title={`${role.name}: ${isView ? 'Autorisé' : 'Interdit'} (Cliquez pour inverser)`}
                                >
                                  {isView ? (
                                    <span className="flex items-center gap-0.5 text-xs font-bold text-emerald-700">
                                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                      {isWrite && <span className="text-[9px] text-amber-700">W</span>}
                                      {isDecide && <span className="text-[9px] text-emerald-700">D</span>}
                                      {isExport && <span className="text-[9px] text-purple-700">X</span>}
                                    </span>
                                  ) : (
                                    <XCircle className="h-4 w-4 text-slate-300" />
                                  )}
                                </button>
                              </Td>
                            );
                          })}
                        </Tr>
                      </>
                    );
                  })}
                </TBody>
              </Table>
            </TableShell>
          </PanelBody>
        </Panel>
      )}

      {/* Modal: Create Custom Role / Account Type */}
      {isCreateRoleOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-2xs overflow-y-auto animate-fade"
            onClick={(e) => {
              if (e.target === e.currentTarget && !createRoleMutation.isPending) {
                setIsCreateRoleOpen(false);
              }
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-4 my-auto animate-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-herb-700" />
                <h3 className="text-base font-bold text-ink">Nouveau Rôle / Type de Compte</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsCreateRoleOpen(false)}
                className="text-ink-soft hover:text-ink text-sm font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3.5 text-xs">
              <div>
                <label className="block font-bold text-ink mb-1">
                  Nom d&apos;affichage du Rôle <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={newRoleForm.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    const slug = name
                      .toLowerCase()
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '')
                      .replace(/[^a-z0-9]+/g, '_')
                      .replace(/^_+|_+$/g, '');
                    setNewRoleForm((prev) => ({
                      ...prev,
                      name,
                      id: prev.id && prev.id !== slug ? prev.id : slug,
                    }));
                  }}
                  placeholder="ex: Comptable & Finance, Préparateur Magasin, Livreur Interne"
                  className="w-full rounded-xl border border-border bg-slate-50 px-3 py-2 text-xs font-semibold text-ink focus:border-herb-600 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-bold text-ink mb-1">
                  Identifiant technique (Slug) <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={newRoleForm.id}
                  onChange={(e) => setNewRoleForm((prev) => ({ ...prev, id: e.target.value }))}
                  placeholder="ex: comptable_finance"
                  className="w-full rounded-xl border border-border bg-slate-50 px-3 py-2 text-xs font-mono text-ink focus:border-herb-600 focus:outline-none"
                />
                <span className="text-[10px] text-ink-ghost mt-0.5 block">
                  Lettres minuscules, chiffres, tirets et underscores uniquement.
                </span>
              </div>

              <div>
                <label className="block font-bold text-ink mb-1">Description & Périmètre</label>
                <textarea
                  rows={2}
                  value={newRoleForm.description}
                  onChange={(e) => setNewRoleForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="ex: Accès aux rapports de livraisons, exports Sage et réconciliation des paiements..."
                  className="w-full rounded-xl border border-border bg-slate-50 px-3 py-2 text-xs text-ink focus:border-herb-600 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-ink mb-1">
                    Rôle Système de Base (Sécurité API) <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={newRoleForm.baseRole}
                    onChange={(e) =>
                      setNewRoleForm((prev) => ({
                        ...prev,
                        baseRole: e.target.value as RoleDefinition['baseRole'],
                      }))
                    }
                    className="w-full rounded-xl border border-border bg-slate-50 px-3 py-2 text-xs font-semibold text-ink focus:border-herb-600 focus:outline-none"
                  >
                    <option value="reviewer">Reviewer (Catalogue / Prix / Sage)</option>
                    <option value="shop_manager">Shop Manager (Logistique / Colis)</option>
                    <option value="supply_chain">Supply Chain (Achats B2B)</option>
                    <option value="viewer">Viewer (Lecture seule)</option>
                    <option value="admin">Admin (Contrôle total)</option>
                  </select>
                  <span className="text-[10px] text-ink-ghost mt-0.5 block">
                    Niveau d&apos;autorisation serveur appliqué aux routes API.
                  </span>
                </div>

                <div>
                  <label className="block font-bold text-ink mb-1">Couleur du Badge</label>
                  <select
                    value={newRoleForm.color}
                    onChange={(e) => setNewRoleForm((prev) => ({ ...prev, color: e.target.value }))}
                    className="w-full rounded-xl border border-border bg-slate-50 px-3 py-2 text-xs font-semibold text-ink focus:border-herb-600 focus:outline-none"
                  >
                    <option value="indigo">Indigo</option>
                    <option value="sky">Bleu Ciel (Sky)</option>
                    <option value="amber">Ambre (Amber)</option>
                    <option value="emerald">Émeraude (Emerald)</option>
                    <option value="purple">Violet (Purple)</option>
                    <option value="rose">Rose (Rose)</option>
                    <option value="teal">Sarcelle (Teal)</option>
                    <option value="cyan">Cyan</option>
                    <option value="orange">Orange</option>
                    <option value="slate">Ardoise (Slate)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-bold text-ink mb-1">Cloner les permissions de départ depuis</label>
                <select
                  value={newRoleForm.cloneFromRoleId}
                  onChange={(e) => setNewRoleForm((prev) => ({ ...prev, cloneFromRoleId: e.target.value }))}
                  className="w-full rounded-xl border border-border bg-slate-50 px-3 py-2 text-xs font-semibold text-ink focus:border-herb-600 focus:outline-none"
                >
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name} ({r.baseRole})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
              <Button variant="ghost" size="sm" onClick={() => setIsCreateRoleOpen(false)}>
                Annuler
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  if (!newRoleForm.name.trim() || !newRoleForm.id.trim()) {
                    alert('Veuillez renseigner le nom et l’identifiant du rôle.');
                    return;
                  }
                  createRoleMutation.mutate(newRoleForm);
                }}
                disabled={createRoleMutation.isPending}
                className="gap-1.5"
              >
                <Plus className="h-4 w-4" />
                <span>{createRoleMutation.isPending ? 'Création en cours…' : 'Créer le rôle'}</span>
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Modal: Copy Permissions from another role */}
      {isCopyModalOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-2xs overflow-y-auto animate-fade"
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsCopyModalOpen(false);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-2xl space-y-4 my-auto animate-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <Copy className="h-4 w-4 text-herb-700" />
                <h3 className="text-sm font-bold text-ink">Copier les permissions vers &laquo; {selectedRole.name} &raquo;</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsCopyModalOpen(false)}
                className="text-ink-soft hover:text-ink text-sm font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <p className="text-ink-soft">
                Sélectionnez le rôle modèle dont vous souhaitez copier toutes les cases de permissions vers{' '}
                <strong>{selectedRole.name}</strong> :
              </p>

              <div>
                <label className="block font-bold text-ink mb-1">Rôle source</label>
                <select
                  value={copySourceRole}
                  onChange={(e) => setCopySourceRole(e.target.value)}
                  className="w-full rounded-xl border border-border bg-slate-50 px-3 py-2 text-xs font-semibold text-ink focus:border-herb-600 focus:outline-none"
                >
                  {roles
                    .filter((r) => r.id !== selectedRoleId)
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.baseRole})
                      </option>
                    ))}
                </select>
              </div>

              <div className="rounded-xl bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-900">
                <span className="font-bold">Attention :</span> Cette opération écrasera la configuration actuelle des
                permissions de <strong>{selectedRole.name}</strong> avec celles du rôle sélectionné.
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
              <Button variant="ghost" size="sm" onClick={() => setIsCopyModalOpen(false)}>
                Annuler
              </Button>
              <Button variant="primary" size="sm" onClick={handleApplyCopyPermissions} className="gap-1.5">
                <Copy className="h-3.5 w-3.5" />
                <span>Appliquer la copie</span>
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Security Architecture Notes */}
      <Panel>
        <PanelBody className="space-y-2 text-xs leading-relaxed text-ink-soft">
          <SectionLabel>Architecture de Sécurité & Rôles MaPara</SectionLabel>
          <p>
            • <strong>Contrôle hybride API + Interface</strong> : Les types de comptes personnalisés s&apos;appuient sur un rôle système de base (<code>baseRole</code>) qui garantit l&apos;étanchéité des routes Fastify, tandis que la matrice granulaire gouverne l&apos;accès interactif aux 36 pages et fonctionnalités de la plateforme.
          </p>
          <p>
            • <strong>Gestion en direct</strong> : Toute modification enregistrée est instantanément synchronisée en base dans les paramètres système (<code>AppSetting: rbac_roles_permissions</code>) et tracée dans le journal d&apos;audit.
          </p>
          <p>
            • <strong>Affectation utilisateur</strong> : Vous pouvez associer directement n&apos;importe quel rôle créé à un utilisateur depuis l&apos;écran <Link to="/settings/users" className="font-bold text-herb-700 hover:underline">Gestion des Utilisateurs</Link>.
          </p>
        </PanelBody>
      </Panel>
    </div>
  );
}
